import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  Snackbar,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { visitsService, Visit, VisitAddendum, VisitWithAddenda } from '@services/visitsService';
import { useAuth } from '@contexts/AuthContext';
import { buildVisitSoapPatch } from '@utils/visitSoapSync';
import { clientsService } from '@services/clientsService';
import { pdfService } from '@services/pdfService';
import { VisitWithContext, VisitFormState, createEmptyFormState } from '@app-types/visit';
import { visitFormSchema, visitSigningSchema } from '@validation/visitFormSchema';
import { getVisitFeatureFlags } from '@config/featureFlags';
import { useVisitPermissions } from '../../hooks/useVisitPermissions';
import { z } from 'zod';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import VisitLockBanner from './VisitLockBanner';
import VisitTimeline from './VisitTimeline';
import AddAddendumDialog from './AddAddendumDialog';
import VitalsCard from './cards/VitalsCard';
import AnamnesisCard from './cards/AnamnesisCard';
import ExamCard from './cards/ExamCard';
import ProblemsCard from './cards/ProblemsCard';
import PlanCard from './cards/PlanCard';
import VaccinationCard from './cards/VaccinationCard';
import TreatmentsCard from './cards/TreatmentsCard';
import BillingCard from './cards/BillingCard';
import AttachmentsCard from './cards/AttachmentsCard';

interface VisitManagementProps {
  visitId: string;
  currentUserId: string;
  onVisitUpdate?: (visit: Visit) => void;
}

const VisitManagement: React.FC<VisitManagementProps> = ({
  visitId,
  currentUserId,
  onVisitUpdate
}) => {
  // Core state
  const [visitContext, setVisitContext] = useState<VisitWithContext | null>(null);
  const [visitData, setVisitData] = useState<VisitWithAddenda | null>(null);
  const [form, setForm] = useState<VisitFormState>(createEmptyFormState());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<z.ZodIssue[]>([]);
  
  // Feature flags
  const [flags] = useState(() => getVisitFeatureFlags());
  
  const { user: authUser } = useAuth();
  const currentUser = useMemo(() => {
    if (authUser?.id) {
      return { id: authUser.id, role: authUser.role || 'veterinarian' };
    }
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : { id: currentUserId, role: 'veterinarian' };
    } catch {
      return { id: currentUserId, role: 'veterinarian' };
    }
  }, [authUser, currentUserId]);

  const clinicalSnapshot = useMemo(() => {
    const v = visitData?.visit as Record<string, unknown> | undefined;
    if (!v) return null;
    return {
      status: String(v.status ?? 'draft'),
      startedAt: (v.started_at as string) || visitContext?.startedAt || null,
      cancelledAt: (v.cancelled_at as string) || null,
      veterinarianId: (v.veterinarian_id as string) || visitContext?.doctorId || null,
    };
  }, [visitData?.visit, visitContext]);

  const permissions = useVisitPermissions(clinicalSnapshot, currentUser, flags);
  
  // Attachment upload state
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  
  // Dialog state
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signatureNotes, setSignatureNotes] = useState('');
  const [signing, setSigning] = useState(false);
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [timelineView, setTimelineView] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ 
    open: false, message: '', severity: 'success' 
  });
  const [versionConflictDialog, setVersionConflictDialog] = useState(false);
  const [serverVersion, setServerVersion] = useState<number | null>(null);
  
  // Refs for autosave
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const formRef = useRef<VisitFormState>(form);
  const versionRef = useRef<number>(1);
  
  // Computed: is this a vaccination visit?
  const isVaccination = (visitContext?.services || []).some(s => s.type === 'vaccination');

  // Load visit data
  useEffect(() => {
    loadVisitData();
  }, [visitId]);

  // Sync form ref
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const handleAutosave = useCallback(async () => {
    try {
      setSaving(true);
      const response = await visitsService.updateVisitForm(visitId, formRef.current);

      if (response && (response as any).version) {
        versionRef.current = (response as any).version;
      }

      setDirty(false);
      setSnackbar({
        open: true,
        message: `Salvat automat la ${new Date().toLocaleTimeString('ro-RO')}`,
        severity: 'success'
      });
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setServerVersion(error.response.data.currentVersion);
        setVersionConflictDialog(true);
        setSnackbar({ open: true, message: 'Conflict de versiune detectat', severity: 'error' });
      } else {
        console.error('Autosave error:', error);
        setSnackbar({ open: true, message: 'Eroare la salvare automată', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  }, [visitId]);

  // Autosave with debounce
  useEffect(() => {
    if (!dirty) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      handleAutosave();
    }, 1200); // 1.2s debounce

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [dirty, form, handleAutosave]);

  const loadVisitData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await visitsService.getVisitWithAddenda(visitId);
      setVisitData(response);

      const visitRow = response.visit as unknown as Record<string, unknown>;
      const internalFromVisit = String(visitRow?.internal_notes ?? '');

      try {
        const contextResponse = await visitsService.getVisitWithContext(visitId);
        setVisitContext(contextResponse);
        versionRef.current = (contextResponse as any).version || 1;

        const initialForm: VisitFormState = {
          vitals: contextResponse.vitals || {},
          anamnesis: contextResponse.anamnesis || {},
          exam: contextResponse.exam || { systems: {} },
          problems: contextResponse.problems || [],
          plan: contextResponse.plan || {},
          vaccination: isVaccination ? (contextResponse.vaccination || { lot: '', exp: '', scheme: 'primar' } as any) : null,
          treatments: contextResponse.treatments || [],
          prescriptions: contextResponse.prescriptions || [],
          billingItems: contextResponse.billingItems || [],
          notes: contextResponse.notes || internalFromVisit,
          recommendations: contextResponse.recommendations || '',
          ownerMessage: '',
          ownerMessageChannel: 'sms',
          publicNote: false,
        };
        setForm(initialForm);
      } catch (e: any) {
        // Context endpoint not available - use legacy data
        console.warn('Context endpoint not available, using legacy data');
        // Initialize with empty form
        if (isVaccination && !form.vaccination) {
          setForm(prev => ({
            ...prev,
            vaccination: { lot: '', exp: '', scheme: 'primar' } as any
          }));
        }
      }
    } catch (error: any) {
      console.error('Error loading visit data:', error);
      setError(error.response?.data?.message || 'Eroare la încărcarea consultației');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = async () => {
    try {
      setSaving(true);
      const vr = visitData?.visit as unknown as Record<string, unknown> | undefined;
      if (vr && permissions.canEdit) {
        const soapPatch = buildVisitSoapPatch(form, vr);
        await visitsService.updateVisitFields(visitId, {
          ...soapPatch,
          recommendations: form.recommendations?.trim() || undefined,
          follow_up_notes: form.plan?.recallReason?.trim() || undefined,
          internal_notes: form.notes?.trim() || undefined,
        });
      }
      const response = await visitsService.updateVisitForm(visitId, form);

      if (response && (response as any).version) {
        versionRef.current = (response as any).version;
      }

      setDirty(false);
      onVisitUpdate?.(visitData!.visit);
      setSnackbar({ open: true, message: 'Salvat cu succes', severity: 'success' });
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setServerVersion(error.response.data.currentVersion);
        setVersionConflictDialog(true);
      }
      setError(error.response?.data?.message || 'Eroare la salvare');
      setSnackbar({ open: true, message: 'Eroare la salvare', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSignVisit = async () => {
    try {
      // Validate for signing
      const parseResult = visitSigningSchema.safeParse(form);
      
      if (!parseResult.success) {
        setValidationErrors(parseResult.error.issues);
        setError('Consultația trebuie completată conform cerințelor minime înainte de semnare.');
        return;
      }
      
      // Additional vaccination check
      if (isVaccination && !form.vaccination) {
        setError('Pentru vizită de vaccinare, câmpurile de vaccinare sunt obligatorii.');
        return;
      }
      
      if (isVaccination && form.vaccination) {
        if (!form.vaccination.lot || !form.vaccination.exp) {
          setError('Pentru vaccinare, seria/lot și data expirării sunt obligatorii înainte de semnare.');
          return;
        }
      }
      
      setSigning(true);

      const vr = visitData?.visit as unknown as Record<string, unknown>;
      const soapPatch = buildVisitSoapPatch(form, vr || {});
      await visitsService.updateVisitFields(visitId, {
        ...soapPatch,
        recommendations: form.recommendations?.trim() || undefined,
        follow_up_notes: form.plan?.recallReason?.trim() || undefined,
        internal_notes: form.notes?.trim() || undefined,
      });

      await visitsService.updateVisitForm(visitId, form);

      const response = await visitsService.signVisit(visitId, signatureNotes);
      setVisitData(prev => prev ? { ...prev, visit: response } : null);
      setSignatureNotes('');
      setSignDialogOpen(false);
      setValidationErrors([]);
      onVisitUpdate?.(response);
      setSnackbar({ open: true, message: 'Consultație semnată cu succes', severity: 'success' });
    } catch (error: any) {
      console.error('Error signing visit:', error);
      setError(error.response?.data?.message || 'Eroare la semnarea consultației');
      setSnackbar({ open: true, message: 'Eroare la semnare', severity: 'error' });
    } finally {
      setSigning(false);
    }
  };

  const handleStartVisit = async () => {
    try {
      const response = await visitsService.startVisit(visitId);
      setVisitData(prev => prev ? { ...prev, visit: response } : null);
      onVisitUpdate?.(response);
      setSnackbar({ open: true, message: 'Consultatia a fost pornita', severity: 'success' });
    } catch (error: any) {
      console.error('Error starting visit:', error);
      setError(error.response?.data?.message || 'Eroare la pornirea consultatiei');
      setSnackbar({ open: true, message: 'Eroare la pornire', severity: 'error' });
    }
  };

  const handleSendRecommendations = async () => {
    try {
      if (!visitContext?.owner?.id) {
        setError('Nu există proprietar asociat pentru trimitere.');
        return;
      }
      
      const content = form.ownerMessage || form.recommendations || 'Mulțumim pentru vizită. Urmați recomandările din fișă.';
      
      // Validate channel and contact
      if (form.ownerMessageChannel === 'sms' || form.ownerMessageChannel === 'whatsapp') {
        if (!visitContext.owner.phone) {
          setError('Proprietarul nu are număr de telefon în baza de date.');
          return;
        }
      }
      
      if (form.ownerMessageChannel === 'email') {
        if (!visitContext.owner.email) {
          setError('Proprietarul nu are email în baza de date.');
          return;
        }
      }
      
      await clientsService.sendMessage([visitContext.owner.id], form.ownerMessageChannel, content);
      setSnackbar({ 
        open: true, 
        message: `Recomandări trimise către proprietar via ${form.ownerMessageChannel.toUpperCase()}`, 
        severity: 'success' 
      });
    } catch (e: any) {
      setError('Nu am putut trimite recomandările.');
      setSnackbar({ open: true, message: 'Eroare la trimiterea recomandărilor', severity: 'error' });
    }
  };

  const handleUploadAttachment = async (file: File) => {
    try {
      setUploading(true);
      const result = await visitsService.uploadAttachment(visitId, file);
      setAttachments(prev => [...prev, result]);
      setSnackbar({ open: true, message: 'Atașament încărcat cu succes', severity: 'success' });
    } catch (error: any) {
      setError('Eroare la încărcarea atașamentului');
      setSnackbar({ open: true, message: 'Eroare la încărcare', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await visitsService.deleteAttachment(visitId, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      setSnackbar({ open: true, message: 'Atașament șters', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Eroare la ștergere', severity: 'error' });
    }
  };

  const handleExportPDF = async (type: 'consultation' | 'vaccination_certificate' | 'recommendations' | 'prescription') => {
    try {
      await pdfService.downloadVisitPDF(visitId, type, visitContext?.animal?.name);
      setSnackbar({ open: true, message: `PDF ${type} descărcat cu succes`, severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Eroare la generarea PDF', severity: 'error' });
    }
  };

  const handleAddendumSuccess = () => {
    loadVisitData();
  };

  const handleResolveConflict = async (action: 'reload' | 'force') => {
    if (action === 'reload') {
      await loadVisitData();
      setVersionConflictDialog(false);
      setDirty(false);
    } else {
      // Force save (risky - poate suprascrie modificări)
      setVersionConflictDialog(false);
      // User acknowledges risk
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: ro });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const map: Record<string, any> = {
      'planned': 'default',
      'arrived': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'error',
      'draft': 'default',
      'signed': 'info',
      'locked': 'error'
    };
    return map[status] || 'default';
  };

  const getOperationalVisitLabel = (v: Visit & Record<string, unknown>) => {
    const st = String(v.status || '').toLowerCase();
    if ((v as any).cancelled_at) return 'Anulată';
    if (st === 'signed') return 'Semnată (finalizată)';
    if (st === 'locked') return 'Blocată';
    if (st === 'draft' || st === 'in_progress') {
      if (v.started_at) return 'În desfășurare';
      return 'Draft — pregătire';
    }
    const map: Record<string, string> = {
      planned: 'Planificată',
      arrived: 'Sosit',
      completed: 'Finalizată',
      cancelled: 'Anulată',
    };
    return map[st] || v.status || '—';
  };

  // Update form helpers
  const updateForm = <K extends keyof VisitFormState>(key: K, value: VisitFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !visitData) {
    return (
      <Alert severity="error">
        <AlertTitle>Eroare</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (!visitData) {
    return (
      <Alert severity="warning">
        <AlertTitle>Consultație nu a fost găsită</AlertTitle>
        Consultația cu ID-ul {visitId} nu a fost găsită.
      </Alert>
    );
  }

  const { visit, addenda } = visitData;

  return (
    <Box sx={{ width: '100%', pb: 4, px: { xs: 2, md: 3 } }}>
      {/* Lock banner */}
      <VisitLockBanner
        visitId={visitId}
        onStatusChange={setCanEdit}
      />

      {/* Save indicator */}
      {dirty && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Modificări nesalvate</Typography>
            {saving && <CircularProgress size={16} />}
            <Button size="small" onClick={handleManualSave} disabled={saving} startIcon={<SaveIcon />}>
              Salvează acum
            </Button>
          </Stack>
        </Alert>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationErrors([])}>
          <AlertTitle>Erori de validare</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((err, idx) => (
              <li key={idx}>{err.path.join('.')}: {err.message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Consultație — {visitContext?.animal?.name || (visit as any).animalName || 'Pacient'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(visit.visit_date || visit.createdAt)}
              </Typography>
              
              {/* Patient & Owner context */}
              {visitContext && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Pacient</Typography>
                  <Typography variant="body2">
                    {visitContext.animal?.name} • {visitContext.animal?.species} {visitContext.animal?.breed && `/ ${visitContext.animal.breed}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {visitContext.animal?.age && `Vârstă: ${visitContext.animal.age} ani`} • Greutate: {form.vitals.weight || visitContext.animal?.weight || '—'} kg
                  </Typography>
                  {visitContext.animal?.microchip && (
                    <Typography variant="caption" color="text.secondary">Microcip: {visitContext.animal.microchip}</Typography>
                  )}
                  
                  {visitContext.animal?.allergies && visitContext.animal.allergies.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="error.main">
                        ⚠️ Alergii: {visitContext.animal.allergies.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="subtitle2" color="text.secondary">Proprietar</Typography>
                  <Typography variant="body2">
                    {visitContext.owner?.firstName} {visitContext.owner?.lastName}
                  </Typography>
                  {visitContext.owner?.phone && (
                    <Typography variant="caption" color="text.secondary">Tel: {visitContext.owner.phone}</Typography>
                  )}
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={getOperationalVisitLabel(visit as unknown as Visit & Record<string, unknown>)}
                color={getStatusColor(visit.status)}
                variant="filled"
                size="small"
              />
              {visitContext?.documentStatus && (
                <Chip
                  label={
                    visitContext.documentStatus === 'draft'
                      ? 'Document: draft'
                      : visitContext.documentStatus === 'signed'
                        ? 'Document: semnat'
                        : 'Document: blocat'
                  }
                  color={getStatusColor(visitContext.documentStatus)}
                  variant="outlined"
                  size="small"
                />
              )}
              <Tooltip title="Vezi timeline">
                <IconButton
                  onClick={() => setTimelineView(!timelineView)}
                  color={timelineView ? 'primary' : 'default'}
                  size="small"
                >
                  <TimelineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Quick actions */}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {permissions.canStartVisit && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleStartVisit}
                color="warning"
                size="small"
              >
                Pornește consultația
              </Button>
            )}

            {permissions.canSign && (
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => setSignDialogOpen(true)}
                color="success"
                disabled={form.problems.length === 0}
                size="small"
              >
                Semnează consultația
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddendumDialogOpen(true)}
              disabled={!permissions.canAddAddendum}
              size="small"
            >
              Addendum
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setTimelineView(!timelineView)}
              size="small"
            >
              {timelineView ? 'Ascunde' : 'Vezi'} Timeline
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleSendRecommendations}
              disabled={!visitContext?.owner || !permissions.canEdit}
              size="small"
            >
              Trimite recomandări
            </Button>

            {/* PDF Export Options */}
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => handleExportPDF('consultation')}
              size="small"
            >
              Fișă consultație
            </Button>

            {isVaccination && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => handleExportPDF('vaccination_certificate')}
                size="small"
                color="warning"
              >
                Certificat vaccin
              </Button>
            )}
          </Stack>

          {/* Permissions & edit window info */}
          {permissions.editReason && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {permissions.editReason}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      {timelineView && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Timeline & Audit</Typography>
            <VisitTimeline visit={visit} addenda={addenda} />
            
            {/* Timestamps */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" display="block" color="text.secondary">
                Creat: {formatDateTime(visit.createdAt)}
              </Typography>
              {visitContext?.arrivedAt && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Sosit: {formatDateTime(visitContext.arrivedAt)}
                </Typography>
              )}
              {visitContext?.startedAt && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Început consultație: {formatDateTime(visitContext.startedAt)}
                </Typography>
              )}
              {visitContext?.endedAt && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Finalizat: {formatDateTime(visitContext.endedAt)}
                </Typography>
              )}
              {visitContext?.signedAt && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Semnat: {formatDateTime(visitContext.signedAt)} de {visitContext.signedBy || 'medic'}
                </Typography>
              )}
              {visitContext?.lockedAt && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Blocat: {formatDateTime(visitContext.lockedAt)}
                </Typography>
              )}
              {visitContext?.qrRedeemedAt && (
                <Typography variant="caption" display="block" color="success.main">
                  ✓ QR validat: {formatDateTime(visitContext.qrRedeemedAt)}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Clinical sections */}
      <VitalsCard
        vitals={form.vitals}
        onChange={(vitals) => updateForm('vitals', vitals)}
        disabled={!permissions.canEdit}
      />

      <AnamnesisCard
        anamnesis={form.anamnesis}
        onChange={(anamnesis) => updateForm('anamnesis', anamnesis)}
        disabled={!permissions.canEdit}
      />

      <ExamCard
        exam={form.exam}
        onChange={(exam) => updateForm('exam', exam)}
        disabled={!permissions.canEdit}
      />

      <ProblemsCard
        problems={form.problems}
        onChange={(problems) => updateForm('problems', problems)}
        disabled={!permissions.canEdit}
      />
      
      {isVaccination && (
        <VaccinationCard
          vaccination={form.vaccination}
          onChange={(vaccination) => updateForm('vaccination', vaccination)}
          disabled={!permissions.canEdit}
          isRequired={isVaccination}
        />
      )}
      
      <TreatmentsCard
        treatments={form.treatments}
        onChange={(treatments) => updateForm('treatments', treatments)}
        disabled={!permissions.canEdit}
      />
      
      <PlanCard
        plan={form.plan}
        onChange={(plan) => updateForm('plan', plan)}
        disabled={!permissions.canEdit}
      />
      
      {flags.enableBilling && (
        <BillingCard
          billingItems={form.billingItems}
          onChange={(items) => updateForm('billingItems', items)}
          disabled={!permissions.canEdit}
        />
      )}

      {/* Attachments */}
      <AttachmentsCard
        attachments={attachments}
        visitId={visitId}
        onUpload={handleUploadAttachment}
        onDelete={handleDeleteAttachment}
        disabled={!permissions.canEdit}
        uploading={uploading}
      />

      {/* Recommendations & Owner Message */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recomandări & Comunicare</Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Note interne (doar clinică — nu se trimit proprietarului)"
            value={form.notes}
            onChange={(e) => updateForm('notes', e.target.value)}
            disabled={!permissions.canEdit}
            placeholder="Observații interne, telefon inter-clinic, etc."
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Recomandări vizibile proprietarului (după finalizare)"
            value={form.recommendations}
            onChange={(e) => updateForm('recommendations', e.target.value)}
            disabled={!permissions.canEdit}
            placeholder="Recomandări post-consultație, instrucțiuni de îngrijire, etc."
            sx={{ mb: 2 }}
          />
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>Trimite mesaj proprietar</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Canal</InputLabel>
              <Select
                value={form.ownerMessageChannel}
                label="Canal"
                onChange={(e) => updateForm('ownerMessageChannel', e.target.value as any)}
                disabled={!permissions.canEdit}
              >
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              size="small"
              label="Mesaj personalizat (opțional)"
              value={form.ownerMessage}
              onChange={(e) => updateForm('ownerMessage', e.target.value)}
              disabled={!permissions.canEdit}
              placeholder="Lasă gol pentru mesaj standard"
            />
            
            <Button
              variant="outlined"
              onClick={handleSendRecommendations}
              disabled={!visitContext?.owner || !permissions.canEdit}
              startIcon={<SendIcon />}
            >
              Trimite
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog open={signDialogOpen} onClose={() => setSignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Semnează Consultația</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Semnarea consultației va marca documentul ca finalizat. 
            {flags.lockVisitAfterHours > 0 && ` După ${flags.lockVisitAfterHours} ore, documentul va fi blocat automat.`}
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Note semnătură (opțional)"
            value={signatureNotes}
            onChange={(e) => setSignatureNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDialogOpen(false)}>Renunță</Button>
          <Button
            variant="contained"
            onClick={handleSignVisit}
            disabled={signing}
            color="success"
          >
            {signing ? <CircularProgress size={20} /> : 'Semnează'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version conflict dialog */}
      <Dialog open={versionConflictDialog} onClose={() => setVersionConflictDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conflict de versiune detectat</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Consultația a fost modificată de altcineva între timp.
            <br />
            Versiunea ta: {versionRef.current}
            <br />
            Versiunea curentă: {serverVersion}
          </Alert>
          <Typography variant="body2">
            Poți să:
          </Typography>
          <ul>
            <li>Reîncarci pagina și pierzi modificările locale (sigur)</li>
            <li>Salvezi forțat și suprascrii modificările serverului (risc)</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleResolveConflict('reload')} color="primary" variant="outlined">
            Reîncarcă pagina
          </Button>
          <Button onClick={() => handleResolveConflict('force')} color="error" variant="contained">
            Salvează forțat (risc)
          </Button>
        </DialogActions>
      </Dialog>

      <AddAddendumDialog
        open={addendumDialogOpen}
        onClose={() => setAddendumDialogOpen(false)}
        visitId={visitId}
        onSuccess={handleAddendumSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VisitManagement;
